/**
 * Schema Import Step — the wizard's first step. It collects the database
 * connection details AND imports the table schema, so there is a single place
 * for everything Redshift-related (the standalone Database step was merged in).
 *
 * Sections, top to bottom:
 *   1. Database configuration — Cluster + Database (FDA presets, editable) and
 *      the three per-environment secret ARNs (Test / Beta / Live).
 *   2. Schema Import tabs (Redshift is the default/first tab):
 *        Tab 1 (Redshift): introspect columns via the backend API (Spec 1),
 *        connecting with whichever env's secret ARN the user selects.
 *        Tab 2 (Paste): DDL/CSV/JSON text parsing via the local schema provider.
 *
 * Both import paths converge on the same ColumnsResponse -> onColumnsLoaded
 * callback, which fires AS SOON AS columns are available. The parent dispatches
 * those columns into the reducer immediately, so the single Cloudscape
 * `<Wizard>` footer "Next" validates and advances. This step renders no forward
 * control of its own.
 */

import React, { useState, useCallback } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  FormField,
  Textarea,
  Button,
  Table,
  Alert,
  Box,
  Input,
  StatusIndicator,
  Tabs,
} from '@amzn/awsui-components-react';

import { schemaProvider } from '../schemaSource';
import { ColumnInfo, ColumnsResponse, OnboardingConfig } from '../types';
import { OnboardingAction, SecretEnv } from '../onboardingReducer';
import { SECRET_ARN_PATTERN } from '../onboardingValidators';
import RedshiftConnectPanel from './RedshiftConnectPanel';

// ─── Constants ─────────────────────────────────────────────────────────

const ACCEPTED_EXTENSIONS = '.sql,.txt,.csv,.json';
const SAMPLE_TRUNCATE = 80;
/** Max characters allowed in the free-text dataset description. */
const BUSINESS_CONTEXT_MAX = 4000;

// ─── Database configuration constants ──────────────────────────────────

const SECRET_ENVS: Array<{ env: SecretEnv; label: string; description: string }> = [
  { env: 'test', label: 'Test secret ARN', description: 'Used for the test phase.' },
  { env: 'beta', label: 'Beta secret ARN', description: 'Used for the beta phase.' },
  { env: 'live', label: 'Live secret ARN', description: 'Used for the live (prod) phase.' },
];

const SECRET_ARN_PLACEHOLDER =
  'arn:aws:secretsmanager:us-east-1:123456789012:secret:bluey-...';

/** Inline validation for a single secret ARN field. */
function secretArnError(arn: string): string | null {
  if (!arn.trim()) return 'Secret ARN is required.';
  if (!SECRET_ARN_PATTERN.test(arn)) return 'Not a valid Secrets Manager ARN.';
  return null;
}

// ─── Column Definitions ────────────────────────────────────────────────

/**
 * Builds the parsed-columns table definitions. The first four columns are
 * read-only metadata; the fifth ("Notes") is an editable per-column input
 * bound to `columnNotes[columnName]`. Editing calls `onColumnNoteChange`,
 * which the wizard wires to the reducer (and standalone usage wires to local
 * state). Notes are OPTIONAL — they never gate navigation.
 */
function buildColumnDefinitions(
  columnNotes: Record<string, string>,
  onColumnNoteChange: (columnName: string, note: string) => void,
) {
  return [
    { id: 'name', header: 'Name', cell: (item: ColumnInfo) => item.columnName },
    { id: 'type', header: 'Type', cell: (item: ColumnInfo) => item.dataType },
    { id: 'nullable', header: 'Nullable', cell: (item: ColumnInfo) => (item.isNullable ? 'Yes' : 'No') },
    {
      id: 'sample',
      header: 'Sample',
      cell: (item: ColumnInfo) => {
        if (!item.sampleValues) return '\u2014';
        const joined = item.sampleValues.join(', ');
        return joined.length > SAMPLE_TRUNCATE ? joined.slice(0, SAMPLE_TRUNCATE) + '\u2026' : joined;
      },
    },
    {
      id: 'notes',
      header: 'Notes (optional)',
      cell: (item: ColumnInfo) => (
        <Input
          value={columnNotes[item.columnName] ?? ''}
          onChange={({ detail }) => onColumnNoteChange(item.columnName, detail.value)}
          placeholder="e.g. revenue in USD"
          ariaLabel={`Notes for ${item.columnName}`}
        />
      ),
    },
  ];
}

// ─── Component ─────────────────────────────────────────────────────────

export interface SchemaImportStepProps {
  /**
   * Called as soon as columns become available — on a successful DDL/CSV/JSON
   * parse OR a successful Redshift introspection. The wizard parent dispatches
   * these into the reducer immediately (LOAD_COLUMNS + inference), which sets
   * `sourceTableName` so the single Wizard footer "Next" passes validation.
   * There is intentionally no in-step Next button.
   */
  onColumnsLoaded?: (result: ColumnsResponse) => void;
  /**
   * Previously-parsed columns to rehydrate on mount. When embedded in the
   * wizard, the parent passes the reducer's persisted columns so navigating
   * back to this step re-shows the parsed table, per-column Notes, and the
   * dataset-description box (the wizard unmounts inactive steps, so local
   * `result` state would otherwise be lost). Omitted in standalone usage.
   */
  initialColumns?: ColumnsResponse | null;
  /**
   * Current dataset-description text. When the wizard owns the reducer it
   * passes `state.config.businessContext` here. Omitted in standalone usage,
   * where the component falls back to its own local state.
   */
  businessContext?: string;
  /** Change handler for the dataset description. Defaults to a no-op. */
  onBusinessContextChange?: (value: string) => void;
  /**
   * Current per-column notes keyed by columnName. When the wizard owns the
   * reducer it passes `state.config.columnNotes` here. Omitted in standalone
   * usage, where the component falls back to its own local state.
   */
  columnNotes?: Record<string, string>;
  /** Change handler for a single column note. Defaults to a no-op. */
  onColumnNoteChange?: (columnName: string, note: string) => void;
  /**
   * The wizard config — supplies the database section's cluster/database and the
   * three secret ARNs. Required when embedded in the wizard; the Database
   * configuration section only renders when this (and `dispatch`) are provided.
   */
  config?: OnboardingConfig;
  /** Reducer dispatch for database-field / secret-ARN edits. */
  dispatch?: React.Dispatch<OnboardingAction>;
}

const SchemaImportStep: React.FC<SchemaImportStepProps> = ({
  onColumnsLoaded,
  initialColumns,
  businessContext,
  onBusinessContextChange,
  columnNotes,
  onColumnNoteChange,
  config,
  dispatch,
}) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [result, setResult] = useState<ColumnsResponse | null>(initialColumns ?? null);
  const [warnings, setWarnings] = useState<string[]>(
    (initialColumns as (ColumnsResponse & { warnings?: string[] }) | null | undefined)?.warnings ?? [],
  );
  const [sourceMode, setSourceMode] = useState<'paste' | 'redshift' | null>(null);

  // ── Standalone fallback state ──────────────────────────────────────
  // When the parent does not supply controlled values/handlers (standalone
  // usage, e.g. the /onboarding/dev route or a unit test), keep context in
  // local state so the inputs stay interactive without breaking compilation.
  const [localBusinessContext, setLocalBusinessContext] = useState('');
  const [localColumnNotes, setLocalColumnNotes] = useState<Record<string, string>>({});

  // Per-env "touched" tracking for the secret ARN fields so we only surface the
  // "required / invalid" error after the user has interacted with a field —
  // otherwise all three would show errors the moment this (first) step loads.
  const [touchedArns, setTouchedArns] = useState<Record<SecretEnv, boolean>>({
    test: false,
    beta: false,
    live: false,
  });

  const businessContextValue = businessContext ?? localBusinessContext;
  const columnNotesValue = columnNotes ?? localColumnNotes;

  const handleBusinessContextChange = useCallback(
    (value: string) => {
      const capped = value.slice(0, BUSINESS_CONTEXT_MAX);
      if (onBusinessContextChange) onBusinessContextChange(capped);
      else setLocalBusinessContext(capped);
    },
    [onBusinessContextChange],
  );

  const handleColumnNoteChange = useCallback(
    (columnName: string, note: string) => {
      if (onColumnNoteChange) onColumnNoteChange(columnName, note);
      else setLocalColumnNotes((prev) => ({ ...prev, [columnName]: note }));
    },
    [onColumnNoteChange],
  );

  const handleParse = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setWarnings([]);
    try {
      const response = await schemaProvider.getColumns({ kind: 'paste', text });
      setResult(response);
      setSourceMode('paste');
      // Surface non-fatal warnings if the provider attaches them
      if ((response as ColumnsResponse & { warnings?: string[] }).warnings) {
        setWarnings((response as ColumnsResponse & { warnings?: string[] }).warnings!);
      }
      // Push columns into the reducer immediately so `sourceTableName` is set
      // and the single Wizard footer "Next" passes validation. No in-step Next.
      if (response.columns.length > 0) {
        onColumnsLoaded?.(response);
      }
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? 'PARSE_ERROR';
      const message = (err as { message?: string }).message ?? 'An unexpected error occurred.';
      setError({ code, message });
    } finally {
      setLoading(false);
    }
  }, [text, onColumnsLoaded]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setText(reader.result);
      }
    };
    reader.readAsText(file);
    // Reset so re-selecting the same file triggers onChange again
    e.target.value = '';
  }, []);

  const handleRedshiftColumnsLoaded = useCallback(
    (columns: ColumnsResponse) => {
      setResult(columns);
      setSourceMode('redshift');
      setWarnings([]);
      setError(null);
      // Same as the paste path: dispatch to the reducer immediately so the
      // Wizard footer "Next" validates against a populated `sourceTableName`.
      if (columns.columns.length > 0) {
        onColumnsLoaded?.(columns);
      }
    },
    [onColumnsLoaded],
  );

  const detectFormat = (): string => {
    const trimmed = text.trimStart();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) return 'JSON';
    if (/CREATE\s+TABLE/i.test(trimmed)) return 'DDL';
    return 'CSV';
  };

  const headerDescription = result
    ? `${result.schema ? result.schema + '.' : ''}${result.tableName} — ${sourceMode === 'redshift' ? 'Redshift introspection' : `detected as ${detectFormat()}`}`
    : undefined;

  const pasteContent = (
    <SpaceBetween size="m">
      <FormField label="Paste DDL, CSV, or JSON">
        <Textarea
          value={text}
          onChange={({ detail }) => setText(detail.value)}
          rows={12}
          placeholder="CREATE TABLE schema.table_name (&#10;  column_name VARCHAR(100),&#10;  ..."
        />
      </FormField>

      <FormField label="Or upload a file" description="Accepted: .sql, .txt, .csv, .json">
        <input
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileChange}
          data-testid="file-upload"
        />
      </FormField>

      <Button
        variant="primary"
        loading={loading}
        onClick={handleParse}
        disabled={!text.trim()}
      >
        Parse
      </Button>
    </SpaceBetween>
  );

  // Database configuration section — only rendered when the wizard supplies
  // config + dispatch. Holds the cluster/database presets and the three secret
  // ARNs; the Redshift tab connects with whichever ARN the user selects.
  const databaseSection =
    config && dispatch ? (
      <Container header={<Header variant="h2">Database configuration</Header>}>
        <SpaceBetween size="l">
          <FormField
            label="Cluster identifier"
            description="FDA preset — edit only if onboarding a non-standard cluster."
            errorText={
              config.database.clusterIdentifier.trim() ? undefined : 'Cluster identifier is required.'
            }
          >
            <Input
              value={config.database.clusterIdentifier}
              onChange={({ detail }) =>
                dispatch({ type: 'SET_DATABASE_FIELD', field: 'clusterIdentifier', value: detail.value })
              }
              ariaLabel="Cluster identifier"
            />
          </FormField>

          <FormField
            label="Database"
            description="FDA preset. Advisory only — at runtime Bluey connects using the database named in the secret."
            errorText={config.database.database.trim() ? undefined : 'Database is required.'}
          >
            <Input
              value={config.database.database}
              onChange={({ detail }) =>
                dispatch({ type: 'SET_DATABASE_FIELD', field: 'database', value: detail.value })
              }
              ariaLabel="Database"
            />
          </FormField>

          {SECRET_ENVS.map(({ env, label, description }) => (
            <FormField
              key={env}
              label={label}
              description={description}
              // Only show the error once the field has been touched, so the user
              // isn't greeted by three "required" errors on first arriving here.
              errorText={
                touchedArns[env] ? (secretArnError(config.database.secretArns[env]) ?? undefined) : undefined
              }
            >
              <Input
                value={config.database.secretArns[env]}
                onChange={({ detail }) => dispatch({ type: 'SET_SECRET_ARN', env, value: detail.value })}
                onBlur={() => setTouchedArns((prev) => (prev[env] ? prev : { ...prev, [env]: true }))}
                placeholder={SECRET_ARN_PLACEHOLDER}
                ariaLabel={label}
              />
            </FormField>
          ))}
        </SpaceBetween>
      </Container>
    ) : null;

  return (
    <SpaceBetween size="l">
      {databaseSection}

      <Container header={<Header variant="h2">Schema Import</Header>}>
        <Tabs
          tabs={[
            {
              id: 'redshift',
              label: 'Connect to Redshift',
              content: (
                <RedshiftConnectPanel
                  secretArns={config?.database.secretArns ?? { test: '', beta: '', live: '' }}
                  onColumnsLoaded={handleRedshiftColumnsLoaded}
                />
              ),
            },
            {
              id: 'paste',
              label: 'Paste DDL / CSV / JSON',
              content: pasteContent,
            },
          ]}
        />
      </Container>

      {error && (
        <Alert
          type="error"
          header={`Parse failed (${error.code})`}
          action={<Button onClick={handleParse}>Retry</Button>}
        >
          {error.message}
        </Alert>
      )}

      {result && (
        <Container
          header={
            <Header
              variant="h2"
              description={headerDescription}
              counter={`(${result.columns.length})`}
            >
              Parsed Columns
            </Header>
          }
        >
          <SpaceBetween size="s">
            {warnings.length > 0 && (
              <Alert type="warning">
                {warnings.map((w, i) => (
                  <Box key={i} variant="p">{w}</Box>
                ))}
              </Alert>
            )}
            <Table
              items={result.columns}
              columnDefinitions={buildColumnDefinitions(columnNotesValue, handleColumnNoteChange)}
              variant="embedded"
              empty={
                <Box textAlign="center" color="inherit">
                  <StatusIndicator type="info">No columns parsed</StatusIndicator>
                </Box>
              }
            />

            <FormField
              label="Describe this dataset (optional — used to improve AI metric & description suggestions later)"
              description="Captured now; consumed by AI metric & description inference in a later phase. Does not affect the Next button."
              constraintText={`${businessContextValue.length} / ${BUSINESS_CONTEXT_MAX} characters`}
            >
              <Textarea
                value={businessContextValue}
                onChange={({ detail }) => handleBusinessContextChange(detail.value)}
                rows={6}
                placeholder="Cerberus is a daily-granularity financial datamart that combines historical actuals, R&O/OP2 forecasts, and curved actuals for Amazon Devices (Fire TV, Echo, Ring, Eero, Blink) across all markets, channels, and P&L accounts — giving a single unified view of units, revenue, and costs for sell-in and sell-through."
                ariaLabel="Describe this dataset"
              />
            </FormField>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default SchemaImportStep;
